import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ForgotPasswordPage — user enters their email and we call the backend to
 * send a reset link. Backend always returns 200 (even for unknown emails) so
 * we can't leak account existence — the confirmation UI is deliberately
 * generic: "if that email is registered, a link is on its way".
 */
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        setError('Something went wrong. Please try again in a minute.');
      } else {
        setSent(true);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen px-5 pt-6 pb-10" style={{ background: '#F7F3EA' }} data-testid="forgot-password-page">
      <Link
        to="/auth"
        className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-[#D6AD60] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to sign in
      </Link>

      <div className="max-w-md mx-auto mt-8">
        <h1
          className="text-3xl mb-2 text-[#173F35]"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
        >
          Forgot your password?
        </h1>
        <p className="text-sm text-stone-600 mb-8 leading-relaxed">
          Enter the email you used to sign up. We&rsquo;ll send a reset link
          straight to your inbox — good for the next 30 minutes.
        </p>

        {sent ? (
          <div
            className="rounded-2xl p-5 border"
            style={{ background: '#FFFCF3', borderColor: 'rgba(214,173,96,0.4)' }}
            data-testid="forgot-password-sent"
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-[#D6AD60]" />
              <h2 className="text-sm font-semibold text-[#173F35]">Check your inbox</h2>
            </div>
            <p className="text-xs text-stone-600 leading-relaxed">
              If <span className="font-semibold text-[#5b4727]">{email.trim()}</span> is
              registered, a reset link is on its way. It may take a minute to arrive —
              check your spam folder just in case.
            </p>
            <Link
              to="/auth"
              className="inline-block mt-4 text-[11px] uppercase tracking-[0.22em] text-[#D6AD60] font-semibold hover:text-[#AA8347]"
              data-testid="forgot-password-back-to-signin"
            >
              Back to sign in →
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-testid="forgot-password-form">
            <label className="text-xs font-medium text-[#D6AD60] mb-1.5 block">Email</label>
            <div className="relative mb-4">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full pl-11 pr-4 py-3 bg-[#FFFCF3] border border-stone-200 rounded-2xl text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-[#D6AD60] focus:ring-2 focus:ring-[#D6AD60]/20 transition-all"
                data-testid="forgot-password-email-input"
              />
            </div>

            {error && (
              <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-2xl">
                <p className="text-xs text-red-500">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 btn-gold rounded-2xl text-sm font-semibold active:scale-[0.98] transition-all duration-300 disabled:opacity-60"
              data-testid="forgot-password-submit-btn"
            >
              {submitting ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
